import { useMemo, useState } from 'react';
import { getSupplierReturnReceiptColumns, getSupplierReturnReceiptDetailColumns } from './cols-definition';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const PREVIEW_ACTION_ORDER = ['export', 'pin'] as const;

export default function HoaDonDichVuTraLaiNhaCungCapPage() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const {
    data,
    isLoading,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData
  } = useCRUD<MuaHangRecord, MuaHangRecord>({
    endpoint: QUERY_KEYS.HOA_DON_DICH_VU_TRA_LAI_NHA_CUNG_CAP
  });
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = (filters: MuaHangRecord) => {};

  const handleSubmit = async (data: MuaHangRecord) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, { ...data, i_so_ct: selectedObj.i_so_ct });
      }
      handleCloseForm();
      refreshData();
      clearSelection();
    } catch (error) {
      return;
    }
  };

  const mainColumns = useMemo(() => getSupplierReturnReceiptColumns(handleViewClick), [handleViewClick]);
  const previewActions = useMemo(
    () => ({
      export: () => console.log('Export clicked'),
      pin: () => console.log('Pin clicked')
    }),
    []
  );

  const tables = [
    {
      name: 'Tất cả',
      rows: data,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: data?.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: data?.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: data?.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Khác',
      rows: data?.filter(row => row.status === '99'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-black' />
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            if (selectedObj) {
              deleteItem(selectedObj.uuid);
            }
            clearSelection();
            handleCloseDelete();
          }}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshData();
              await fetchDetail(selectedObj?.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='min-h-0 flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='hddv-tra-lai-ncc-master-table' className='h-full w-full overflow-hidden'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  selectedRowId={selectedRowIndex || undefined}
                  pageSize={20}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='hddv-tra-lai-ncc-bottom-detail-table' className='h-full overflow-hidden'>
                <ErpInputTableV2
                  rows={selectedObj && !isLoading ? detail?.chi_tiet || [] : []}
                  columns={getSupplierReturnReceiptDetailColumns}
                  showIndexColumn
                  actions={previewActions}
                  actionOrder={PREVIEW_ACTION_ORDER as MuaHangRecord}
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height={'100%'}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
    </div>
  );
}
